import { useState, type CSSProperties } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  CalendarClock,
  Check,
  Eye,
  FileText,
  GitCompareArrows,
  ShieldCheck,
  Maximize2,
} from "lucide-react";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { LocaleLink } from "@/components/locale-link";
import { useTranslation } from "@/common/providers/i18n-provider";
import { DEMO_URL, SIGN_UP_LEDGER } from "@/config/product-links";
import { productUrl } from "@/lib/product-attribution";
import type { ImageSizes } from "@/lib/image-sizes";
import { TRACKING_FAQ_KEYS } from "@/pages/tracking-content";
import { ScreenshotPreview } from "@/components/screenshot-preview";
import "@/styles/tracking.css";

/** The only screenshots this page renders; verified against the generated catalog. */
const IMAGE_SIZES = {
  "/img/ledger/income-statement-apple.webp": { width: 1600, height: 1000 },
  "/img/ledger/balance-sheet-apple.webp": { width: 1600, height: 1000 },
  "/img/ledger/trial-balance-example.webp": { width: 1600, height: 1000 },
  "/img/ledger/overview-example.webp": { width: 1600, height: 1000 },
  "/img/ledger/overview-apple.webp": { width: 1600, height: 1000 },
  "/img/ledger/journal-example.webp": { width: 1600, height: 1000 },
  "/img/ledger/overview-crypto.webp": { width: 1600, height: 1000 },
} satisfies ImageSizes;
type TrackingImageSrc = keyof typeof IMAGE_SIZES;

const REPORT_IMAGES = [
  [
    "/img/ledger/income-statement-apple.webp",
    "tracking.preview.income",
    [280, 220, 1296, 780],
    [280, 625, 490, 360],
  ],
  [
    "/img/ledger/balance-sheet-apple.webp",
    "tracking.preview.balance",
    [280, 220, 1296, 780],
    [280, 625, 490, 360],
  ],
  [
    "/img/ledger/trial-balance-example.webp",
    "tracking.preview.trial",
    [280, 220, 1296, 780],
    [1085, 220, 490, 360],
  ],
  [
    "/img/ledger/overview-example.webp",
    "tracking.preview.overview",
    [280, 220, 1296, 780],
    [300, 460, 490, 360],
  ],
] as const;

const OWNERSHIP_POINTS: {
  icon: LucideIcon;
  titleKey: string;
  bodyKey: string;
  linkTo?: string;
  linkKey?: string;
}[] = [
  {
    icon: Eye,
    titleKey: "tracking.differentiator.plainText.title",
    bodyKey: "tracking.differentiator.plainText.body",
  },
  {
    icon: GitCompareArrows,
    titleKey: "tracking.differentiator.git.title",
    bodyKey: "tracking.differentiator.git.body",
    linkTo: "/git",
    linkKey: "tracking.differentiator.git.link",
  },
  {
    icon: ShieldCheck,
    titleKey: "tracking.differentiator.auditable.title",
    bodyKey: "tracking.differentiator.auditable.body",
  },
  {
    icon: FileText,
    titleKey: "tracking.differentiator.portable.title",
    bodyKey: "tracking.differentiator.portable.body",
    linkTo: "/security",
    linkKey: "tracking.differentiator.portable.link",
  },
];

function Screenshot({
  src,
  alt,
  priority = false,
  crop,
  mobileCrop = crop,
}: {
  src: TrackingImageSrc;
  alt: string;
  priority?: boolean;
  crop: readonly [number, number, number, number];
  mobileCrop?: readonly [number, number, number, number];
}) {
  const { t } = useTranslation();
  const { width, height } = IMAGE_SIZES[src];

  const cropStyle = (rect: typeof crop, prefix: string) => ({
    [`--${prefix}-ratio`]: `${rect[2]} / ${rect[3]}`,
    [`--${prefix}-width`]: `${(width / rect[2]) * 100}%`,
    [`--${prefix}-left`]: `${(-rect[0] / rect[2]) * 100}%`,
    [`--${prefix}-top`]: `${(-rect[1] / rect[3]) * 100}%`,
  });
  return (
    <ScreenshotPreview
      src={src}
      alt={alt}
      width={width}
      height={height}
      label={t("tracking.preview.enlarge")}
      className="tracking-preview-trigger"
    >
      <span
        className="tracking-crop"
        style={
          {
            ...cropStyle(crop, "desktop-crop"),
            ...cropStyle(mobileCrop, "mobile-crop"),
          } as CSSProperties
        }
      >
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          decoding="async"
        />
      </span>
      <span className="tracking-preview-caption">
        <span>{alt}</span>
        <span>
          <Maximize2 size={16} aria-hidden="true" />
          {t("tracking.preview.enlarge")}
        </span>
      </span>
    </ScreenshotPreview>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="tracking-bullets">
      {items.map((item) => (
        <li key={item}>
          <Check size={18} aria-hidden="true" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function SectionLink({ to, label }: { to: string; label: string }) {
  return (
    <LocaleLink to={to} className="tracking-link">
      {label}
      <ArrowRight size={17} aria-hidden="true" />
    </LocaleLink>
  );
}

export function TrackingComponent() {
  const { t } = useTranslation();
  const [reportIndex, setReportIndex] = useState(0);
  const [reportSrc, reportLabel, reportCrop, reportMobileCrop] =
    REPORT_IMAGES[reportIndex];
  return (
    <div className="tracking-page">
      <AppHeader />
      <main id="main">
        <section className="tracking-hero">
          <div className="tracking-container">
            <div className="tracking-hero-copy">
              <div>
                <p className="tracking-label">{t("tracking.hero.eyebrow")}</p>
                <h1>{t("tracking.hero.title")}</h1>
              </div>
              <div className="tracking-hero-intro">
                <p>{t("tracking.hero.description")}</p>
                <div className="tracking-actions">
                  <a
                    className="tracking-button tracking-primary"
                    href={productUrl(SIGN_UP_LEDGER, "tracking")}
                  >
                    {t("homepage.startFree")}
                    <ArrowRight size={18} aria-hidden="true" />
                  </a>
                  <SectionLink
                    to="/fava"
                    label={t("tracking.hero.secondaryCta")}
                  />
                </div>
              </div>
            </div>
            <figure className="tracking-hero-preview">
              <Screenshot
                src="/img/ledger/overview-apple.webp"
                alt={t("tracking.netWorth.image.alt")}
                priority
                crop={[280, 366, 1296, 515]}
                mobileCrop={[300, 460, 470, 260]}
              />
              <figcaption>
                <span>{t("tracking.hero.badge.netWorth")}</span>
                <span>{t("tracking.hero.badge.everyAccount")}</span>
                <span>{t("tracking.hero.badge.plainText")}</span>
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="tracking-section">
          <div className="tracking-container tracking-split">
            <div className="tracking-heading">
              <h2>{t("tracking.netWorth.title")}</h2>
              <p>{t("tracking.netWorth.description")}</p>
              <SectionLink to="/fava" label={t("tracking.netWorth.link")} />
            </div>
            <BulletList
              items={[
                t("tracking.netWorth.bullet.1"),
                t("tracking.netWorth.bullet.2"),
                t("tracking.netWorth.bullet.3"),
              ]}
            />
          </div>
        </section>

        <section className="tracking-section tracking-tint">
          <div className="tracking-container tracking-feature">
            <div className="tracking-heading">
              <p className="tracking-label">
                {t("tracking.transactions.eyebrow")}
              </p>
              <h2>{t("tracking.transactions.title")}</h2>
              <p>{t("tracking.transactions.description")}</p>
              <BulletList
                items={[
                  t("tracking.transactions.bullet.1"),
                  t("tracking.transactions.bullet.2"),
                  t("tracking.transactions.bullet.3"),
                ]}
              />
              <div className="tracking-actions">
                <SectionLink
                  to="/smart-import"
                  label={t("tracking.transactions.link.smartImport")}
                />
                <SectionLink
                  to="/download"
                  label={t("tracking.transactions.link.download")}
                />
              </div>
            </div>
            <figure className="tracking-shot">
              <Screenshot
                src="/img/ledger/journal-example.webp"
                alt={t("tracking.preview.journal")}
                crop={[280, 262, 620, 338]}
                mobileCrop={[475, 265, 410, 335]}
              />
            </figure>
          </div>
        </section>

        <section className="tracking-section" id="reports">
          <div className="tracking-container">
            <div className="tracking-split">
              <div className="tracking-heading">
                <p className="tracking-label">
                  {t("tracking.reports.eyebrow")}
                </p>
                <h2>{t("tracking.reports.title")}</h2>
                <p>{t("tracking.reports.description")}</p>
              </div>
              <div>
                <BulletList
                  items={[
                    t("tracking.reports.bullet.1"),
                    t("tracking.reports.bullet.2"),
                    t("tracking.reports.bullet.3"),
                  ]}
                />
                <SectionLink to="/fava" label={t("tracking.reports.link")} />
              </div>
            </div>
            <div
              className="tracking-report-selector"
              role="group"
              aria-label={t("tracking.reports.eyebrow")}
            >
              {REPORT_IMAGES.map(([, label], index) => (
                <button
                  key={label}
                  type="button"
                  aria-pressed={reportIndex === index}
                  aria-controls="tracking-report-preview"
                  onClick={() => setReportIndex(index)}
                >
                  {t(label)}
                </button>
              ))}
            </div>
            <div
              id="tracking-report-preview"
              className="tracking-report-preview"
            >
              <Screenshot
                key={reportSrc}
                src={reportSrc}
                alt={t(reportLabel)}
                crop={reportCrop}
                mobileCrop={reportMobileCrop}
              />
            </div>
          </div>
        </section>

        <section className="tracking-section tracking-tint">
          <div className="tracking-container tracking-feature tracking-investments">
            <figure className="tracking-shot">
              <Screenshot
                src="/img/ledger/overview-crypto.webp"
                alt={t("tracking.preview.portfolio")}
                crop={[1110, 366, 466, 515]}
                mobileCrop={[1125, 452, 445, 425]}
              />
            </figure>
            <div className="tracking-heading">
              <p className="tracking-label">
                {t("tracking.investments.eyebrow")}
              </p>
              <h2>{t("tracking.investments.title")}</h2>
              <p>{t("tracking.investments.description")}</p>
              <BulletList
                items={[
                  t("tracking.investments.bullet.1"),
                  t("tracking.investments.bullet.2"),
                  t("tracking.investments.bullet.3"),
                ]}
              />
              <SectionLink to="/fava" label={t("tracking.investments.link")} />
            </div>
          </div>
        </section>

        <section className="tracking-section">
          <div className="tracking-container tracking-planning">
            <div className="tracking-heading">
              <CalendarClock size={28} aria-hidden="true" />
              <h2>{t("tracking.planAhead.title")}</h2>
              <p>{t("tracking.planAhead.description")}</p>
              <SectionLink to="/fava" label={t("tracking.planAhead.link")} />
            </div>
            <BulletList
              items={[
                t("tracking.planAhead.bullet.1"),
                t("tracking.planAhead.bullet.2"),
                t("tracking.planAhead.bullet.3"),
              ]}
            />
          </div>
        </section>

        <section className="tracking-section tracking-connect">
          <div className="tracking-container">
            <div className="tracking-heading">
              <h2>{t("tracking.dashboard.title")}</h2>
              <p>{t("tracking.dashboard.description")}</p>
            </div>
            <div className="tracking-connect-options">
              <article>
                <h3>{t("tracking.dashboard.link.bankSync")}</h3>
                <p>{t("tracking.dashboard.bullet.1")}</p>
                <SectionLink
                  to="/bank-sync"
                  label={t("tracking.dashboard.link.bankSync")}
                />
              </article>
              <article>
                <h3>{t("tracking.dashboard.link.linkAccounts")}</h3>
                <p>{t("tracking.dashboard.bullet.2")}</p>
                <SectionLink
                  to="/link-accounts"
                  label={t("tracking.dashboard.link.linkAccounts")}
                />
              </article>
              <article>
                <h3>{t("tracking.dashboard.link.smartImport")}</h3>
                <p>{t("tracking.dashboard.bullet.3")}</p>
                <SectionLink
                  to="/smart-import"
                  label={t("tracking.dashboard.link.smartImport")}
                />
              </article>
            </div>
          </div>
        </section>

        <section className="tracking-section tracking-ownership">
          <div className="tracking-container tracking-split">
            <div className="tracking-heading">
              <p className="tracking-label">
                {t("tracking.differentiator.eyebrow")}
              </p>
              <h2>{t("tracking.differentiator.title")}</h2>
              <p>{t("tracking.differentiator.description")}</p>
            </div>
            <div className="tracking-ownership-points">
              {OWNERSHIP_POINTS.map((point) => (
                <article key={point.titleKey}>
                  <point.icon size={22} aria-hidden="true" />
                  <h3>{t(point.titleKey)}</h3>
                  <p>{t(point.bodyKey)}</p>
                  {point.linkTo && point.linkKey ? (
                    <SectionLink to={point.linkTo} label={t(point.linkKey)} />
                  ) : null}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="tracking-section">
          <div className="tracking-container tracking-faq-layout">
            <div className="tracking-heading">
              <h2>{t("tracking.faq.title")}</h2>
              <p>{t("tracking.faq.description")}</p>
            </div>
            <div className="tracking-faq">
              {TRACKING_FAQ_KEYS.map(([questionKey, answerKey]) => (
                <details key={questionKey}>
                  <summary>
                    {t(questionKey)}
                    <span aria-hidden="true">+</span>
                  </summary>
                  <p>{t(answerKey)}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="tracking-final">
          <div className="tracking-container tracking-final-inner">
            <div className="tracking-heading">
              <h2>{t("tracking.cta.title")}</h2>
              <p>{t("tracking.cta.description")}</p>
            </div>
            <div className="tracking-actions">
              <a
                className="tracking-button tracking-primary"
                href={productUrl(SIGN_UP_LEDGER, "tracking")}
              >
                {t("homepage.startFree")}
                <ArrowRight size={18} aria-hidden="true" />
              </a>
              <a
                className="tracking-button tracking-secondary"
                href={productUrl(DEMO_URL, "tracking")}
              >
                {t("homepage.tryDemo")}
              </a>
            </div>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
